import { observer } from 'mobx-react-lite'
import { FormColumn, FormColumns, FormRow } from '@/components/Form'
import { FormGroupContainer } from '@/components/FormContainer'
import { TextAreaContainer } from '@/components/inputs/TextAreaContainer'
import { TextInputContainer } from '@/components/inputs/TextInputContainer'
import { match } from '@/utils/pattern-match-util'
import { AdvisorSelectContainer } from '@/features/iam/invitations/components/AdvisorSelectContainer'
import {
  BulkUploadForm,
  EntryModeSelector,
  SignInTypeSelector,
  InvitationDialog,
  InvitationResultsDialog,
} from '@/features/iam/invitations/components/InvitationDialog'
import { downloadCustomerCsvTemplate } from '@/features/iam/customers/customer-csv'
import { type ClientInvitationDialogViewModel } from './ClientInvitationDialogViewModel'

const testId = 'client-invitation'

export interface ClientInvitationDialogContainerProps {
  vm: ClientInvitationDialogViewModel
}

export const ClientInvitationDialogContainer = observer(
  function ClientInvitationDialogContainer({
    vm,
  }: ClientInvitationDialogContainerProps) {
    if (vm.showBulkResults) {
      return (
        <InvitationResultsDialog
          testId={testId}
          showing={vm.modal.showing}
          onDone={vm.dismissBulkResults}
          onRetryFailed={vm.retryFailedBulkResults}
          successfulResults={vm.successfulBulkResults}
          failedResults={vm.failedBulkResults}
        />
      )
    }

    return (
      <InvitationDialog
        title="Invite client"
        description={vm.dialogDescription}
        testId={testId}
        showing={vm.modal.showing}
        onClose={vm.close}
        signInType={vm.signInType}
        bulkUploadMode={vm.bulkUploadMode}
        validBulkCustomerCount={vm.validBulkCustomers.length}
        emailAddressCount={vm.emailAddresses.length}
        onSubmit={vm.submit}
        isSubmitPending={vm.submit.pending}
      >
        {vm.canUseAccessLink && vm.canUseUsernameAndPassword && (
          <SignInTypeSelector
            signInType={vm.signInType}
            onSignInTypeChange={vm.setSignInType}
            isDisabled={vm.submit.pending}
          />
        )}

        {match(vm.signInType, {
          AccessLink: () => <CustomerInvitationFormContainer vm={vm} />,
          UsernamePassword: () => <RegularInvitationFormContainer vm={vm} />,
        })}

        {vm.canChooseAdvisor && vm.signInType === 'AccessLink' && (
          <AdvisorSelectContainer
            vm={vm.advisorSelect}
            isDisabled={vm.submit.pending}
          />
        )}
      </InvitationDialog>
    )
  },
)

interface InvitationFormContainerProps {
  vm: ClientInvitationDialogViewModel
}

const CustomerInvitationFormContainer = observer(
  function CustomerInvitationFormContainer({
    vm,
  }: InvitationFormContainerProps) {
    return (
      <>
        <EntryModeSelector
          bulkUploadMode={vm.bulkUploadMode}
          onBulkUploadModeChange={vm.setBulkUploadMode}
          isDisabled={vm.submit.pending}
        />

        {vm.bulkUploadMode ? (
          <BulkUploadForm
            testId={testId}
            fileName={vm.bulkUploadFileName}
            error={vm.bulkUploadError}
            customers={vm.bulkCustomers.map((customer) => ({
              ...customer,
              errors: customer.errors.slice(),
            }))}
            validCustomerCount={vm.validBulkCustomers.length}
            isDisabled={vm.submit.pending}
            onFileSelected={vm.handleCsvUpload}
            onRemoveCustomer={vm.removeBulkCustomer}
            onDownloadTemplate={downloadCustomerCsvTemplate}
          />
        ) : (
          <SingleCustomerFormContainer vm={vm} />
        )}
      </>
    )
  },
)

const SingleCustomerFormContainer = observer(
  function SingleCustomerFormContainer({ vm }: InvitationFormContainerProps) {
    return (
      <>
        <FormRow>
          <FormGroupContainer
            label="Email address"
            error={vm.customerValidation.getError('customerEmail')}
          >
            <TextInputContainer
              model={vm}
              field="customerEmail"
              type="email"
              placeholder="customer@example.com"
              isDisabled={vm.submit.pending}
              data-test={`${testId}-customer-email`}
            />
          </FormGroupContainer>
        </FormRow>

        <FormRow>
          <FormColumns>
            <FormColumn>
              <FormGroupContainer
                label="First name"
                error={vm.customerValidation.getError('customerGivenName')}
              >
                <TextInputContainer
                  model={vm}
                  field="customerGivenName"
                  placeholder="John"
                  isDisabled={vm.submit.pending}
                  data-test={`${testId}-customer-given-name`}
                />
              </FormGroupContainer>
            </FormColumn>
            <FormColumn>
              <FormGroupContainer
                label="Last name"
                error={vm.customerValidation.getError('customerFamilyName')}
              >
                <TextInputContainer
                  model={vm}
                  field="customerFamilyName"
                  placeholder="Doe"
                  isDisabled={vm.submit.pending}
                  data-test={`${testId}-customer-family-name`}
                />
              </FormGroupContainer>
            </FormColumn>
          </FormColumns>
        </FormRow>

        <FormRow>
          <FormGroupContainer
            label="Phone number"
            sublabel="(optional)"
            error={vm.customerValidation.getError('customerPhoneNumber')}
          >
            <TextInputContainer
              model={vm}
              field="customerPhoneNumber"
              type="tel"
              placeholder="+1 555-123-4567"
              isDisabled={vm.submit.pending}
              data-test={`${testId}-customer-phone`}
            />
          </FormGroupContainer>
        </FormRow>
      </>
    )
  },
)

const RegularInvitationFormContainer = observer(
  function RegularInvitationFormContainer({
    vm,
  }: InvitationFormContainerProps) {
    return (
      <>
        <FormRow>
          <FormGroupContainer
            label="Email address"
            error={vm.invitationValidation.getError('emailAddressTextArea')}
          >
            <TextAreaContainer
              model={vm}
              field="emailAddressTextArea"
              rows={3}
              placeholder="e.g. timmy.taxes@taxfyle.com, cindy.cpa@firm.com"
              help="Enter one or more email addresses, separated by commas."
              data-test={`${testId}-email-textarea`}
            />
          </FormGroupContainer>
        </FormRow>
      </>
    )
  },
)
